import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Briefcase, Clock, Building, Linkedin, Share2 } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobDetail = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJob();
  }, [id]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${id}`);
      setJob(response.data);
    } catch (error) {
      console.error('Error fetching job:', error);
    } finally {
      setLoading(false);
    }
  };

  const shareOnLinkedIn = () => {
    const jobUrl = `https://vocavi.in/jobs/${job.id}`;
    const title = encodeURIComponent(`${job.title} - ${job.location}`);
    const summary = encodeURIComponent(`${job.title} opportunity at ${job.sector} sector. ${job.experience} experience required. Apply now through Vocavi Partners!`);
    const linkedInUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(jobUrl)}&title=${title}&summary=${summary}&source=Vocavi%20Partners`;
    window.open(linkedInUrl, '_blank', 'width=600,height=600');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="job-detail-loading">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="job-detail-not-found">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Job not found</p>
          <Link to="/jobs" className="text-secondary font-semibold">
            Back to Jobs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO 
        title={`${job.title} - ${job.location} | Vocavi Partners Careers`}
        description={`${job.title} position in ${job.location}. ${job.experience} experience required. ${job.sector} sector. Apply now through Vocavi Partners.`}
        keywords={`${job.title}, ${job.sector}, ${job.location}, executive jobs India, leadership positions`}
        canonical={`https://vocavi.in/jobs/${job.id}`}
        type="website"
      />
      {/* Back Button */}
      <div className="pt-24 pb-8 bg-white sticky top-20 z-40 border-b" data-testid="job-detail-header">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <Link
            to="/jobs"
            data-testid="back-to-jobs"
            className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Jobs
          </Link>
          <button
            onClick={shareOnLinkedIn}
            data-testid="share-linkedin-button"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white hover:bg-[#0077B5]/90 transition-colors text-sm font-medium"
          >
            <Linkedin size={18} />
            Share on LinkedIn
          </button>
        </div>
      </div>

      {/* Job Details */}
      <section className="section-spacing bg-white" data-testid="job-detail-content">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl lg:text-5xl font-bold text-primary mb-6" data-testid="job-title">
              {job.title}
            </h1>

            <div className="flex flex-wrap gap-6 text-muted-foreground mb-8" data-testid="job-meta">
              <span className="flex items-center">
                <MapPin size={20} className="mr-2" />
                {job.location}
              </span>
              <span className="flex items-center">
                <Briefcase size={20} className="mr-2" />
                {job.sector}
              </span>
              <span className="flex items-center">
                <Clock size={20} className="mr-2" />
                {job.experience}
              </span>
              <span className="flex items-center">
                <Building size={20} className="mr-2" />
                {job.employment_type}
              </span>
            </div>

            <div className="prose prose-lg max-w-none mb-12">
              <h2 className="text-2xl font-bold text-primary mb-4">About the Role</h2>
              <p className="text-muted-foreground leading-relaxed">{job.description}</p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-primary mb-4">Key Responsibilities</h2>
              <ul className="space-y-3" data-testid="job-responsibilities">
                {job.responsibilities.map((responsibility, index) => (
                  <li key={index} className="flex items-start">
                    <span className="inline-block w-2 h-2 bg-secondary mt-2 mr-3 flex-shrink-0" />
                    <span className="text-muted-foreground">{responsibility}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-primary mb-4">Requirements</h2>
              <ul className="space-y-3" data-testid="job-requirements">
                {job.requirements.map((requirement, index) => (
                  <li key={index} className="flex items-start">
                    <span className="inline-block w-2 h-2 bg-accent mt-2 mr-3 flex-shrink-0" />
                    <span className="text-muted-foreground">{requirement}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-muted p-8 text-center">
              <h3 className="text-2xl font-bold text-primary mb-4">Interested in This Role?</h3>
              <p className="text-muted-foreground mb-6">
                Submit your resume or get in touch with our team to learn more about this opportunity.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  data-testid="apply-now-button"
                  className="bg-primary text-white hover:bg-primary/90 px-8 py-4 uppercase tracking-widest text-sm font-semibold transition-all inline-block"
                >
                  Apply Now
                </Link>
                <a
                  href="mailto:connect@vocavi.in"
                  data-testid="email-contact-button"
                  className="border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-4 uppercase tracking-widest text-sm font-semibold transition-all inline-block"
                >
                  Email Us
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default JobDetail;